/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";

export default styled(Box)(({ theme, ownerState }) => {
  const { palette, functions, borders, boxShadows } = theme;
  const { variant, bgColor, color, opacity, borderRadius, shadow, coloredShadow } = ownerState;

  const { gradients, grey, white, surface, ink, borderColor } = palette;
  const { linearGradient } = functions;
  const { borderRadius: radius } = borders;
  const { colored } = boxShadows;

  const greyColors = {
    "grey-100": grey[100],
    "grey-200": grey[200],
    "grey-300": grey[300],
    "grey-400": grey[400],
    "grey-500": grey[500],
    "grey-600": grey[600],
    "grey-700": grey[700],
    "grey-800": grey[800],
    "grey-900": grey[900],
  };

  const validGradients = [
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
  ];

  const validColors = [
    "transparent",
    "white",
    "black",
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
    "text",
    "grey-100",
    "grey-200",
    "grey-300",
    "grey-400",
    "grey-500",
    "grey-600",
    "grey-700",
    "grey-800",
    "grey-900",
  ];

  const validBorderRadius = ["xs", "sm", "md", "lg", "xl", "xxl", "section"];
  const validBoxShadows = ["xs", "sm", "md", "lg", "xl", "xxl", "inset"];

  // Redesign (Phase 1): page-header chrome uses variant="gradient" + borderRadius="lg".
  // It now renders as a FLAT NEUTRAL SURFACE (ink text, hairline underline, no colored
  // shadow) consistently across all sidenav modes/presets — retiring the template's blue
  // gradient title bars app-wide while keeping the MDBox API intact. Other gradient uses
  // (icon boxes borderRadius="xl", charts) keep their gradient until the owning page is
  // migrated to typographic tiles (Phase 4+).
  const isPageHeader = variant === "gradient" && borderRadius === "lg";

  const surfaceColor = surface ? surface.main : white.main;
  const inkColor = ink ? ink.main : white.main;
  const hairline = borderColor ? borderColor.main : "#DCE3EA";

  // background value
  let backgroundValue = bgColor;

  if (variant === "gradient") {
    if (isPageHeader) {
      backgroundValue = surfaceColor;
    } else {
      const hasValidGradient = validGradients.find((el) => el === bgColor);
      backgroundValue = hasValidGradient
        ? linearGradient(gradients[bgColor].main, gradients[bgColor].state)
        : white.main;
    }
  } else if (validColors.find((el) => el === bgColor)) {
    backgroundValue = palette[bgColor] ? palette[bgColor].main : greyColors[bgColor];
  } else {
    backgroundValue = bgColor;
  }

  // color value
  let colorValue = color;

  if (validColors.find((el) => el === color)) {
    colorValue = palette[color] ? palette[color].main : greyColors[color];
  }

  // Page-header text is ink regardless of the color prop the page passed (kills white-on-white).
  if (isPageHeader) {
    colorValue = inkColor;
  }

  // borderRadius value
  let borderRadiusValue = borderRadius;

  if (validBorderRadius.find((el) => el === borderRadius)) {
    borderRadiusValue = radius[borderRadius];
  }

  // boxShadow value
  let boxShadowValue = "none";

  if (validBoxShadows.find((el) => el === shadow)) {
    boxShadowValue = boxShadows[shadow];
  } else if (coloredShadow) {
    boxShadowValue = colored[coloredShadow] ? colored[coloredShadow] : "none";
  }

  // Page headers never carry a colored shadow in the redesign.
  if (isPageHeader) {
    boxShadowValue = "none";
  }

  // Flat page-header chrome: compact padding, ink title text, hairline underline.
  const pageHeaderExtras = isPageHeader
    ? {
        paddingTop: "0.75rem !important",
        paddingBottom: "0.75rem !important",
        borderBottom: `1px solid ${hairline}`,
        "& .MuiTypography-root": {
          color: `${inkColor} !important`,
        },
      }
    : {};

  return {
    opacity,
    background: backgroundValue,
    color: colorValue,
    borderRadius: borderRadiusValue,
    boxShadow: boxShadowValue,
    ...pageHeaderExtras,
  };
});
